import {
    ConversationClassification,
    ConversationCounterparty,
    CounterpartyStatus,
    MessageIntent,
} from "../models/conversation.models";

const CLASSIFICATION_VALUES: ReadonlySet<string> = new Set(Object.values(ConversationClassification));
const COUNTERPARTY_VALUES: ReadonlySet<string> = new Set(Object.values(ConversationCounterparty));

/** Translation key `smart_inbox.inbox_view.detail.<prefix>_<value>`; `fallback` for an unknown value. */
function labelKey(prefix: string, known: ReadonlySet<string>, fallback: string, value?: string | null): string {
    return `smart_inbox.inbox_view.detail.${prefix}_${value && known.has(value) ? value : fallback}`;
}

/**
 * Sinks "undefined" to the end of a category rollup so a thread that also carries
 * a real category never leads with it. Order is otherwise preserved (the backend
 * returns most-recent-first).
 */
export function orderCategories(categories: ConversationClassification[]): ConversationClassification[] {
    const isUndefined = (c: ConversationClassification) => c === ConversationClassification.UNDEFINED;
    return [...categories.filter((c) => !isUndefined(c)), ...categories.filter(isUndefined)];
}

/** Whether the AI placed this message; the stored `'undefined'` category does not count. */
export function isClassified(classification?: string | null): boolean {
    return !!classification && classification !== ConversationClassification.UNDEFINED;
}

/** Translation key for a category, falling back to "undefined" when absent. */
export function classificationLabelKey(classification?: string | null): string {
    return labelKey('classification', CLASSIFICATION_VALUES, ConversationClassification.UNDEFINED, classification);
}

/**
 * Translation key for an intent. The classifier owns an open list, so an intent
 * we have no translation for falls back to `humanizeIntent()` at render time —
 * see `intentLabel()`.
 */
export function intentLabelKey(intent: MessageIntent): string {
    return `smart_inbox.inbox_view.detail.intent_${intent.toLowerCase()}`;
}

/** `ORDER_PLACING` → `Order placing`. Fallback for intents we don't translate. */
export function humanizeIntent(intent: MessageIntent): string {
    const words = intent.replace(/_/g, ' ').trim().toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
}

// ── Counterparty ────────────────────────────────────────────────────────────

/** `customer` is the normal case on a sales mailbox, so it gets no badge. */
const BADGED_COUNTERPARTIES: ReadonlySet<ConversationCounterparty> = new Set([
    ConversationCounterparty.THIRD_PARTY,
    ConversationCounterparty.UNKNOWN,
]);

/** The counterparty to badge, or `undefined` when there is nothing to show. */
export function badgedCounterparty(counterparty?: ConversationCounterparty | null): ConversationCounterparty | undefined {
    return counterparty && BADGED_COUNTERPARTIES.has(counterparty) ? counterparty : undefined;
}

/** `dashed` for `unknown`, `tint` for a decided value. */
export function counterpartyTagVariant(counterparty: ConversationCounterparty): 'tint' | 'dashed' {
    return counterparty === ConversationCounterparty.UNKNOWN ? 'dashed' : 'tint';
}

/** Filter chip variant: a selected chip always gets its tone, `unknown` included. */
export function counterpartyFilterVariant(selected: boolean): 'tint' | 'muted' {
    return selected ? 'tint' : 'muted';
}

/** Translation key for a counterparty, falling back to "unknown" when absent. */
export function counterpartyLabelKey(counterparty?: string | null): string {
    return labelKey('counterparty', COUNTERPARTY_VALUES, ConversationCounterparty.UNKNOWN, counterparty);
}

/** A stored value read as a counterparty; absent or unrecognized values read `unknown`. */
export function toCounterparty(value?: string | null): ConversationCounterparty {
    return storedCounterparty(value) ?? ConversationCounterparty.UNKNOWN;
}

/** The stored counterparty, or `undefined` when the message carries none. */
export function storedCounterparty(value?: string | null): ConversationCounterparty | undefined {
    return value && COUNTERPARTY_VALUES.has(value) ? (value as ConversationCounterparty) : undefined;
}

/** Whether the verdict still needs a human eye; the status is derived server-side. */
export function isToVerify(status?: string | null): boolean {
    return status === CounterpartyStatus.TO_VERIFY;
}

export function isConfirmed(status?: string | null): boolean {
    return status === CounterpartyStatus.CONFIRMED;
}

/** The confidence as a whole percentage, only for a verdict to verify. */
export function counterpartyConfidencePercent(
    confidence?: number | null,
    status?: string | null,
): number | null {
    if (!isToVerify(status) || confidence === null || confidence === undefined) return null;
    return Math.round(confidence * 100);
}

/** Translation key for the source of a counterparty; an unknown source shows its raw key. */
export function counterpartySourceLabelKey(source?: string | null): string {
    if (!source) return 'smart_inbox.inbox_view.detail.counterparty_source_none';
    return `smart_inbox.inbox_view.detail.counterparty_source_${source}`;
}

/** Whether the AI disagreed with the verdict on this message; computed server-side. */
export function hasCounterpartyConflict(conflict?: boolean | null): boolean {
    return conflict === true;
}

/** Whether the linked company conflicts with the conversation's counterparty. */
export function counterpartyDisagreesWithCompany(conversation?: {
    counterparty?: ConversationCounterparty | null;
    counterparty_status?: string | null;
    counterparty_conflict?: boolean | null;
} | null): boolean {
    if (!conversation) return false;
    return hasCounterpartyConflict(conversation.counterparty_conflict)
        || (conversation.counterparty === ConversationCounterparty.THIRD_PARTY
            && isConfirmed(conversation.counterparty_status));
}

/** The AI's own read of this message for the tooltip, or `null` when it gave none. */
export function counterpartyModelReading(
    model?: string | null,
    confidence?: number | null,
): { value: string; percent: number } | null {
    if (!model || model === ConversationCounterparty.UNKNOWN) return null;
    return { value: model, percent: Math.round((confidence ?? 0) * 100) };
}

/** Confirming `customer` needs a company: it is written to the customer registry. */
export function confirmNeedsCompany(counterparty: ConversationCounterparty): boolean {
    return counterparty === ConversationCounterparty.CUSTOMER;
}
